import {
  DatabaseFieldType,
  type DatabaseField,
  type DatabaseValue
} from '@databases/types/database';
import { type DatabaseChange } from '@databases/utils/database/changesets';
import { type Dispatch, type SetStateAction } from 'react';
import { type IntlShape } from 'react-intl-next';

import { getDateDefaultValueModeSelectConfig } from './date/DateDefaultValueModeSelectConfig.ts';
import {
  type DefaultValueMode,
  type DefaultValueModeSelectConfig
} from './shared/DefaultValueModeSelect.tsx';
import { getUserDefaultValueModeSelectConfig } from './user/UserDefaultValueModeSelectConfig.ts';

type DefaultValueModeSelectConfigArgs = {
  addChangeToDraft: (change: DatabaseChange) => void;
  dateFieldSettingsEnabled: boolean;
  effectiveDefaultValue: DatabaseValue;
  field: DatabaseField;
  formatMessage: IntlShape['formatMessage'];
  optimisticDefaultValueMode: DefaultValueMode | null;
  setOptimisticDefaultValue: Dispatch<SetStateAction<DatabaseValue | null>>;
  setOptimisticDefaultValueMode: Dispatch<SetStateAction<DefaultValueMode | null>>;
};

export const getDefaultValueModeSelectConfig = ({
  addChangeToDraft,
  dateFieldSettingsEnabled,
  effectiveDefaultValue,
  field,
  formatMessage,
  optimisticDefaultValueMode,
  setOptimisticDefaultValue,
  setOptimisticDefaultValueMode
}: DefaultValueModeSelectConfigArgs): DefaultValueModeSelectConfig | null => {
  switch (field.type) {
    case DatabaseFieldType.DATE:
      return getDateDefaultValueModeSelectConfig({
        addChangeToDraft,
        effectiveDefaultValue,
        field: field as DatabaseField<DatabaseFieldType.DATE>,
        formatMessage,
        includeTime: dateFieldSettingsEnabled && Boolean(field.settings.timeFormat),
        optimisticDefaultValueMode,
        setOptimisticDefaultValue,
        setOptimisticDefaultValueMode
      });

    case DatabaseFieldType.USER:
      return getUserDefaultValueModeSelectConfig({
        addChangeToDraft,
        effectiveDefaultValue,
        field: field as DatabaseField<DatabaseFieldType.USER>,
        formatMessage,
        optimisticDefaultValueMode,
        setOptimisticDefaultValue,
        setOptimisticDefaultValueMode
      });

    default:
      return null;
  }
};
